/* Copyright (c) 2020-present CloudIO Inc */

import { IoExternalDs, Query } from '@cloudio-saas/datasource-types';
import { FormControl } from '@mui/material';
import { useStoreRecords } from 'components/DataStoreReference/DataStoreReference';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import React, { memo, useEffect } from 'react';
import { useStoreBusy } from 'store/useAppStore';
import useDataStore from 'store/useDataStore';
import WaveDots from 'utils/WaveDots';

export const extDsFilter: (appUid: string) => Query<IoExternalDs> = (
  appUid: string,
) => ({
  filter: [{ appUid: { is: appUid } }],
  limit: 2000,
  offset: 0,
  projection: {
    externalDsUid: 1,
  },
});

function ExternalDataSourceSelect({
  appUid,
  disabled,
  label,
  onChange,
  required,
  value,
}: {
  appUid: string;
  disabled?: boolean;
  label: string;
  onChange: (val: string) => void;
  required?: boolean;
  value: string;
}) {
  const store = useDataStore<IoExternalDs>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoExternalDs',
    alias: `IoExternalDs_select_alias_${appUid}`,
  });
  useEffect(() => {
    store.query({ props: extDsFilter(appUid) });
  }, [appUid, store]);
  const records = useStoreRecords(store);
  const busy = useStoreBusy(store);
  const handleChange = (externalDsUid: string, option: Option | null) => {
    if (externalDsUid !== value) {
      onChange(externalDsUid);
    }
  };

  return (
    <FormControl
      component="fieldset"
      disabled={disabled}
      fullWidth
      required={required}
      variant="standard"
    >
      <VirtualizedSelectInput
        disabled={busy || disabled}
        label={label}
        labelAttribute="externalDsUid"
        onChange={handleChange}
        options={records}
        placeholder={busy ? 'Loading ...' : 'Choose an External DataSource'}
        startIcon={busy ? <WaveDots /> : null}
        value={value}
        valueAttribute="externalDsUid"
        variant="standard"
      />
    </FormControl>
  );
}

export default memo(ExternalDataSourceSelect);
